<script lang="ts" setup>
import { type OtInputNumberState } from '@1trading/design-system';
import type { PropType } from 'vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import BigNumber from '@exchange/helpers/bignumber';
import numberFormat from '@exchange/helpers/number-format';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import type { MarketModel } from '@exchange/libs/market/service/src';
import { OrderSide } from '@exchange/libs/order/shared-model/src/lib/order-essentials';

type OrderSizeInputTooltip = {
  content: string;
  theme: string;
  shown: boolean;
  placement: string;
  distance: number;
};

const props = defineProps({
  amountTooltip: { type: Object as PropType<OrderSizeInputTooltip | null>, required: false },
  amountValue: { type: String as PropType<string | null> },
  autofocus: { type: Boolean, default: false },
  inputState: { type: String as PropType<OtInputNumberState>, default: 'default' },
  market: { type: Object as PropType<MarketModel>, required: true },
  orderSide: { type: String as PropType<OrderSide>, required: true },
  totalTooltip: { type: Object as PropType<OrderSizeInputTooltip | null>, required: false },
  totalValue: { type: String as PropType<string | null> },
});
const emit = defineEmits<{
  (e: 'input-focus', value: FocusEvent): void;
  (e: 'input-blur', value: FocusEvent): void;
  (e: 'update:totalValue', value: string): void;
  (e: 'update:amountValue', value: string): void;
}>();

const { isXS } = useMQBreakpoints();
const { locale } = useI18n({ useScope: 'global' });
const isTotalShown = ref(false);

const asNumber = (value?: string): number => {
  if (!value) {
    return 0;
  }

  return new BigNumber(value).toNumber();
};

const formatSubtitle = (precision: number, currency: string, value?: string): string =>
  `${numberFormat({ number: asNumber(value), precision, locale: locale.value as string })} ${currency}`;

const formattedTotal = computed(() => formatSubtitle(props.market.marketPrecision, props.market.quote.code, props.totalValue));
const formattedAmount = computed(() => formatSubtitle(props.market.amountPrecision, props.market.base.code, props.amountValue));

const amountSubtitle = computed(() => (isXS.value ? formattedTotal.value : undefined));
const totalSubtitle = computed(() => (isXS.value ? formattedAmount.value : undefined));
const totalState = computed(() => (isXS.value ? props.inputState : 'default'));

const onSwitcherBtnClick = () => {
  isTotalShown.value = !isTotalShown.value;
};

const onUpdateTotal = ($event: string) => {
  emit('update:totalValue', $event);
};
const onUpdateAmount = ($event: string) => {
  emit('update:amountValue', $event);
};

const onInputFocus = ($event: FocusEvent) => {
  emit('input-focus', $event);
};
const onInputBlur = ($event: FocusEvent) => {
  emit('input-blur', $event);
};
</script>

<template>
  <div class="order-size-input">
    <div :class="`order-size-input__${isXS ? 'switcher' : 'joint-input'}`">
      <x-input-number
        v-if="!isXS || !isTotalShown"
        v-tooltip="amountTooltip"
        class="order-size-input__amount"
        :value="amountValue"
        name="amountValue"
        :label="$t('modules.orderForm.amount')"
        :autofocus="autofocus"
        :state="inputState"
        variant="dark"
        :compact="true"
        :currency="market.base.code"
        :precision="market.amountPrecision"
        :subvalue="amountSubtitle"
        testid="order-amount"
        :lpignore="true"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @update:value="onUpdateAmount"
      />
      <x-input-number
        v-if="!isXS || isTotalShown"
        v-tooltip="totalTooltip"
        class="order-size-input__total"
        :value="totalValue"
        name="totalValue"
        :label="$t('modules.orderForm.total')"
        variant="dark"
        :compact="true"
        :state="totalState"
        :precision="market.marketPrecision"
        :currency="market.quote.code"
        :subvalue="totalSubtitle"
        :lpignore="true"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @update:value="onUpdateTotal"
      />
      <x-button
        v-if="isXS"
        class="order-size-input__switcher-button"
        @click="onSwitcherBtnClick"
      >
        <ot-icon
          class="order-size-input__switcher-button-icon"
          name="repeat"
        />
      </x-button>
    </div>
  </div>
</template>

<style lang="scss">
.order-size-input {
  &__switcher {
    display: flex;
    gap: 4px;

    .order-size-input__amount,
    .order-size-input__total {
      height: 64px;
      flex: 1;
    }
  }

  &__switcher-button {
    padding-right: 10px;
    padding-left: 10px;
  }

  &__switcher-button-icon {
    width: 18px;
    height: 18px;
  }

  &__joint-input {
    position: relative;
    display: grid;
    grid-auto-columns: 1fr 1fr;
    grid-auto-flow: column;
    grid-gap: 8px;

    &::before {
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 50%;
      display: flex;
      width: 24px;
      height: 24px;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background-color: rgb(var(--v-theme-elevation-1));
      color: rgb(var(--v-theme-text-primary));
      content: '\2248'; // Almost Equal To
      font-size: var(--font-size-xs);
      transform: translate(-50%, -50%);
    }
  }
}
</style>
